import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  CircularProgress,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { JsonTable } from "./JsonTable";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
  overflowY: "auto",
};

const isBulkResponse = (response) =>
  response &&
  typeof response === "object" &&
  !Array.isArray(response) &&
  Object.values(response).every((res) => res?.data || res?.error);

const isImageMime = (mime) => mime?.startsWith("image/");

const downloadFile = (file) => {
  const link = document.createElement("a");
  link.href = `data:${file.mimeType};base64,${file.base64}`;
  link.download = file.fileName;
  link.click();
};

const EmmResponseModal = ({ isOpen, onClose, response, isLoading }) => {
  if (!isOpen) return null;

  const renderDeviceBlock = (response, id) => {
    const data = response?.data || response;

    // ❌ ERROR
    if (response?.success === false) {
      return (
        <Paper key={id} sx={{ mb: 2, p: 2 }}>
          <Typography color="error">
            ❌ {response.error || "Something went wrong"}
          </Typography>
        </Paper>
      );
    }

    // 📦 FILE RESPONSE — handles both array format and files object format
    const files = Array.isArray(data) ? data : data?.files;

    if (files?.length) {
      return (
        <Paper
          key={id}
          sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: "none" }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Device: {id}
          </Typography>

          {files.map((file, idx) => {
            const isImage = isImageMime(file.mimeType);
            const isText = file.mimeType === "text/plain";
            let textContent = null;

            if (isText) {
              try {
                textContent = atob(file.base64);
              } catch (e) {
                textContent = "Error decoding file";
              }
            }

            return (
              <Box
                key={idx}
                sx={{ mt: 2, borderTop: "1px solid #eee", pt: 2 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {file.fileName}
                </Typography>

                {/* 🖼 Image Preview */}
                {isImage && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={`data:${file.mimeType};base64,${file.base64}`}
                      alt={file.fileName}
                      style={{ maxWidth: "100%", borderRadius: 8 }}
                    />
                  </Box>
                )}

                {/* 📄 Text Preview */}
                {isText && textContent && (
                  <Paper
                    sx={{
                      p: 1.5,
                      mt: 1,
                      background: "#f5f5f5",
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {textContent}
                    </pre>
                  </Paper>
                )}

                {/* ⬇️ Download only for non-text files */}
                {!isText && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => downloadFile(file)}
                  >
                    Download
                  </Button>
                )}
              </Box>
            );
          })}
        </Paper>
      );
    }

    // 🖼 OLD FORMAT (base64 screenshot)
    const content = data?.content;
    const isBase64Image =
      content && typeof content === "string" && content.startsWith("iVBOR");

    if (isBase64Image) {
      return (
        <Paper
          key={id}
          sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: "none" }}
        >
          <Typography variant="subtitle1">
            Device: {data?.deviceAlias || id}
          </Typography>

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <img
              src={`data:image/png;base64,${content}`}
              alt="Screenshot"
              style={{ maxWidth: "100%", borderRadius: 8 }}
            />
          </Box>
        </Paper>
      );
    }

    // ✅ SIMPLE STRING
    if (typeof data === "string") {
      return (
        <Paper key={id} sx={{ mb: 2, p: 2 }}>
          <Typography>✅ {data}</Typography>
        </Paper>
      );
    }

    // 🧾 FALLBACK JSON
    return (
      <Paper
        key={id}
        variant="outlined"
        sx={{ mb: 3, p: 2, borderRadius: 2, border: "none", boxShadow: "none" }}
      >
        <Typography variant="subtitle1">
          Device: {data?.deviceAlias || id}
        </Typography>

        {data && <JsonTable data={data} />}
      </Paper>
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={style}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="1rem"
        >
          <Typography variant="h6">Device Response(s)</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box textAlign="center" py={5}>
            <CircularProgress />
            <Typography variant="body2" mt={2}>
              Fetching response...
            </Typography>
          </Box>
        ) : isBulkResponse(response) ? (
          Object.entries(response).map(([id, res]) =>
            renderDeviceBlock(res, id)
          )
        ) : (
          renderDeviceBlock(
            response,
            response?.data?.deviceAlias || "Unknown"
          )
        )}
      </Box>
    </Modal>
  );
};

export default EmmResponseModal;